import { ComposedChart, Bar, Line, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import InfoTip from "./InfoTip";
import { VerticalCursor, TipBox } from "./FlowChart";
import { money, dpct, k, niceMax, ticksTo, COLORS } from "./metricsMath";

const SALES = "#002FA7";

const SalesTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  const lines = [[SALES, "Venta neta", money(r.ventas)], [null, "Notas", String(r.notas)]];
  if (r.notas) lines.push([null, "Ticket promedio", money(r.ventas / r.notas)]);
  if (r.ventasCrec != null) lines.push([null, "Cambio vs mes anterior", dpct(r.ventasCrec), r.ventasCrec > 0 ? "text-[#00873D]" : r.ventasCrec < 0 ? "text-[#E0000F]" : "text-zinc-500"]);
  lines.push([null, "Acumulado del año", money(r.ventasAcum)]);
  return <TipBox testid="sales-tooltip" title={r.mesLargo} lines={lines} />;
};

// Monto arriba de cada barra y, debajo del mes, el cambio contra el mes anterior.
const MonthTick = ({ x, y, payload, rows }) => {
  const r = rows[payload.index];
  return (
    <g transform={`translate(${x},${y})`}>
      <text y={12} textAnchor="middle" fontSize={11} fill="#71717a">{payload.value}</text>
      {r?.ventasCrec != null && <text y={28} textAnchor="middle" fontSize={11} fontWeight={700} fill={r.ventasCrec > 0 ? COLORS.in : r.ventasCrec < 0 ? COLORS.out : "#71717a"} data-testid={`sales-change-${r.month}`}>{dpct(r.ventasCrec, 0)}</text>}
    </g>
  );
};

const AmountLabel = ({ x, y, width, value, index, rows }) => (value > 0 ? <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#18181b" data-testid={`sales-label-${rows[index]?.month}`}>{k(value)}</text> : null);

// Venta neta por mes (barras) + acumulado del año (línea, eje derecho).
export default function SalesChart({ rows, total, count, year }) {
  const top = niceMax(Math.max(0, ...rows.map((r) => r.ventas || 0)));
  const topAcc = niceMax(Math.max(0, ...rows.map((r) => r.ventasAcum || 0)));
  const withSales = rows.filter((r) => r.ventas > 0);
  const best = withSales.length ? withSales.reduce((a, b) => (b.ventas > a.ventas ? b : a)) : null;
  return (
    <section className="border border-zinc-200 bg-white rounded-lg p-5 flex flex-col gap-2 min-w-0" data-testid="sales-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-bold text-base m-0 inline-flex items-center gap-1.5">Venta neta por mes <InfoTip k="sales" /></h3>
          <p className="text-xs text-zinc-500 m-0 mt-1 leading-relaxed">Notas de venta contabilizadas (pagadas o con abonos; las canceladas no cuentan). Arriba de cada barra el monto; debajo del mes, el cambio contra el mes anterior.</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-zinc-500">Total vendido {year}</div>
          <div className="font-heading text-2xl font-extrabold numeric text-[#002FA7] leading-tight" data-testid="sales-total">{money(total)}</div>
          <div className="text-xs text-zinc-500 numeric" data-testid="sales-sub">{count} nota{count === 1 ? "" : "s"}{withSales.length ? ` · promedio ${money(total / withSales.length)}/mes` : ""}{best ? ` · mejor mes: ${best.mes}` : ""}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-zinc-500" data-testid="sales-legend">
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: SALES }} />Venta neta del mes</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3 h-0.5 inline-block rounded" style={{ background: COLORS.gas }} />Acumulado del año (eje derecho)</span>
        <span className="inline-flex items-center gap-1.5"><b className="text-[#00873D]">+%</b> Cambio vs mes anterior</span>
      </div>
      <div className="h-[320px]" data-testid="sales-chart">
        {withSales.length === 0 ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-zinc-400">Sin ventas contabilizadas en {year}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 22, right: 8, left: 0, bottom: 20 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
              <XAxis dataKey="mes" interval={0} tick={<MonthTick rows={rows} />} axisLine={{ stroke: "#d4d4d8" }} tickLine={false} height={40} />
              <YAxis yAxisId="v" tickFormatter={k} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={48} domain={[0, top]} ticks={ticksTo(top)} />
              <YAxis yAxisId="acc" orientation="right" tickFormatter={k} tick={{ fontSize: 11, fill: COLORS.gas }} axisLine={false} tickLine={false} width={52} domain={[0, topAcc]} ticks={ticksTo(topAcc)} />
              <Tooltip content={<SalesTip />} cursor={<VerticalCursor />} />
              <Bar yAxisId="v" dataKey="ventas" name="Venta neta" fill={SALES} radius={[3, 3, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                <LabelList dataKey="ventas" content={<AmountLabel rows={rows} />} />
              </Bar>
              <Line yAxisId="acc" type="monotone" dataKey="ventasAcum" name="Acumulado" stroke={COLORS.gas} strokeWidth={2} dot={{ r: 3, fill: COLORS.gas, strokeWidth: 0 }} activeDot={{ r: 5 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
